/**
 * Auth-aware sign-in trigger.
 *
 * Two visual variants:
 *   • variant="icon"  → small round person silhouette (used in the site header)
 *   • variant="pill"  → icon + "Sign in" text pill (used in the footer)
 *
 * Behaviour:
 *   • Not signed in → clicking opens the multi-portal SignInModal.
 *   • Signed in to Portal → shows a dropdown with the current session
 *     (Admin · <name>), a "Go to admin" link and Sign out.
 *   • Also aware of Emerald Books sessions (books_token in localStorage) so
 *     the dropdown lists any active Books tenant too, with its own Sign out.
 *
 * Extending later: to add a new portal (e.g. Client Portal after launch),
 * just add another block in the "sessions" derivation below and a matching
 * PORTALS entry in SignInModal.jsx.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserCircle2, LogIn, ShieldCheck, BookOpen, LogOut, ChevronDown, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SignInModal from './SignInModal';

const readJson = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
};

const useBooksSession = () => {
  const [session, setSession] = useState(() => ({
    token: typeof window !== 'undefined' ? localStorage.getItem('books_token') : null,
    user: readJson('books_user'),
    tenant: readJson('books_tenant'),
  }));

  useEffect(() => {
    const sync = () => setSession({
      token: localStorage.getItem('books_token'),
      user: readJson('books_user'),
      tenant: readJson('books_tenant'),
    });
    window.addEventListener('storage', sync);
    // Also poll on focus (localStorage `storage` event only fires cross-tab)
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const signOut = () => {
    ['books_token', 'books_user', 'books_tenant'].forEach((k) => localStorage.removeItem(k));
    setSession({ token: null, user: null, tenant: null });
  };

  return { ...session, signOut };
};

const SignInWidget = ({ variant = 'icon', className = '' }) => {
  const { user: portalUser, logout: portalLogout } = useAuth();
  const books = useBooksSession();
  const [modalOpen, setModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  const anySession = !!portalUser || !!books.token;

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleTriggerClick = () => {
    if (anySession) setMenuOpen((v) => !v);
    else setModalOpen(true);
  };

  const label = useMemo(() => {
    if (portalUser) return `${portalUser.name || portalUser.username || 'Admin'}`;
    if (books.user) return books.tenant?.company_name || books.user.email;
    return 'Sign in';
  }, [portalUser, books]);

  return (
    <>
      <div className={`relative ${className}`} ref={wrapRef}>
        {variant === 'icon' ? (
          <button
            onClick={handleTriggerClick}
            data-testid="signin-widget-icon"
            aria-label={anySession ? 'Account menu' : 'Sign in'}
            title={anySession ? label : 'Sign in'}
            className={
              'w-9 h-9 rounded-full flex items-center justify-center transition-colors ' +
              (anySession
                ? 'bg-[#0f5c3d] text-white hover:bg-[#0b1a2e]'
                : 'bg-white/70 text-[#0f5c3d] hover:bg-white border border-[#0f5c3d]/20')
            }
          >
            <UserCircle2 className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={handleTriggerClick}
            data-testid="signin-widget-pill"
            className={
              'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ' +
              (anySession
                ? 'bg-[#0f5c3d] text-white hover:bg-[#0b1a2e]'
                : 'bg-white text-[#0f5c3d] hover:bg-white/90 border border-[#0f5c3d]/20 hover:border-[#0f5c3d]/40')
            }
          >
            <UserCircle2 className="w-4 h-4" />
            <span className="truncate max-w-[140px]">{anySession ? label : 'Sign in'}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>
        )}

        {/* Auth-aware dropdown */}
        {menuOpen && anySession && (
          <div
            className={
              'absolute z-50 min-w-[260px] rounded-xl bg-white shadow-xl border border-black/5 overflow-hidden ' +
              (variant === 'icon' ? 'right-0 top-full mt-2' : 'right-0 bottom-full mb-2')
            }
            data-testid="signin-widget-menu"
          >
            <div className="px-4 py-3 border-b border-black/5 bg-[#f7fbf8]">
              <div className="text-xs uppercase tracking-wider text-[#0b1a2e]/50 font-medium">Signed in</div>
            </div>

            {portalUser && (
              <div className="px-4 py-3 flex items-start gap-3 border-b border-black/5">
                <div className="w-8 h-8 rounded-md bg-[#0f5c3d]/12 text-[#0f5c3d] flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0b1a2e]">Admin · {portalUser.name || portalUser.username}</div>
                  <div className="text-xs text-[#0b1a2e]/50 truncate">{portalUser.email || portalUser.role}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      className="inline-flex items-center gap-1 text-xs text-[#0f5c3d] hover:underline"
                      data-testid="signin-widget-goto-admin"
                    >
                      Open portal <ExternalLink className="w-3 h-3" />
                    </Link>
                    <span className="text-[#0b1a2e]/20">·</span>
                    <button
                      onClick={async () => { await portalLogout(); setMenuOpen(false); }}
                      className="inline-flex items-center gap-1 text-xs text-rose-600 hover:underline"
                      data-testid="signin-widget-signout-admin"
                    >
                      <LogOut className="w-3 h-3" /> Sign out
                    </button>
                  </div>
                </div>
              </div>
            )}

            {books.token && books.user && (
              <div className="px-4 py-3 flex items-start gap-3 border-b border-black/5">
                <div className="w-8 h-8 rounded-md bg-[#0b7c53]/12 text-[#0b7c53] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0b1a2e] truncate">
                    Books · {books.tenant?.company_name || 'Emerald Books'}
                  </div>
                  <div className="text-xs text-[#0b1a2e]/50 truncate">{books.user.email}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Link
                      to="/books/app/dashboard"
                      onClick={() => setMenuOpen(false)}
                      className="inline-flex items-center gap-1 text-xs text-[#0f5c3d] hover:underline"
                      data-testid="signin-widget-goto-books"
                    >
                      Open Books <ExternalLink className="w-3 h-3" />
                    </Link>
                    <span className="text-[#0b1a2e]/20">·</span>
                    <button
                      onClick={() => { books.signOut(); setMenuOpen(false); }}
                      className="inline-flex items-center gap-1 text-xs text-rose-600 hover:underline"
                      data-testid="signin-widget-signout-books"
                    >
                      <LogOut className="w-3 h-3" /> Sign out
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => { setMenuOpen(false); setModalOpen(true); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#0b1a2e] hover:bg-[#f7fbf8] transition-colors"
              data-testid="signin-widget-switch"
            >
              <LogIn className="w-4 h-4 text-[#0f5c3d]" />
              Sign in to another portal
            </button>
          </div>
        )}
      </div>

      <SignInModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default SignInWidget;
